import { useBrandEquity } from '../../hooks/useBrandEquity'
import DBEIHero from './DBEIHero'
import DBEISkeleton from './DBEISkeleton'
import PillarBreakdown from './PillarBreakdown'
import PillarSkeleton from './PillarSkeleton'
import ChannelStrength from './ChannelStrength'
import ChannelSkeleton from './ChannelSkeleton'
import MetricDetails from './MetricDetails'
import MetricTableSkeleton from './MetricTableSkeleton'
import NarrativeSection from './NarrativeSection'
import { Card, CardContent } from '../../../../components/ui/card'
import { Button } from '../../../../components/ui/button'

interface BrandEquityDashboardProps {
  userId: string
}

export default function BrandEquityDashboard({ userId }: BrandEquityDashboardProps) {
  const { report, isLoading, isCalculating, isDownloading, isRegeneratingNarrative, error, recalculate, downloadPdf, regenerateNarrative } =
    useBrandEquity(userId)

  if (isLoading) {
    return (
      <div className="space-y-6">
        <DBEISkeleton />
        <PillarSkeleton />
        <ChannelSkeleton />
        <MetricTableSkeleton />
      </div>
    )
  }

  if (error && !report) {
    return (
      <Card>
        <CardContent className="p-10 text-center">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3" />
          <p className="text-sm font-medium text-gray-600">{error}</p>
        </CardContent>
      </Card>
    )
  }

  if (!report) {
    return (
      <Card>
        <CardContent className="p-10 text-center">
          <i className="ri-bar-chart-grouped-line text-4xl text-gray-300 mb-3" />
          <p className="text-sm font-medium text-gray-600">No Brand Equity data available</p>
          <p className="text-xs text-gray-400 mt-1">Click Calculate to generate your first report</p>
        </CardContent>
      </Card>
    )
  }

  const csChannels = report.pillars?.CS?.channels || {}

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Brand Equity Dashboard</h2>
          <p className="text-xs text-gray-500">
            Last calculated: {report.calculation_date}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadPdf}
            disabled={isDownloading}
            className="text-gray-600"
          >
            {isDownloading ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1" />
                Downloading...
              </>
            ) : (
              <>
                <i className="ri-download-line mr-1" />
                Download PDF
              </>
            )}
          </Button>
          <Button
            size="sm"
            onClick={recalculate}
            disabled={isCalculating}
            className="bg-[#5C4FCF] hover:bg-[#4a40b8] text-white"
          >
            {isCalculating ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1" />
                Calculating...
              </>
            ) : (
              <>
                <i className="ri-refresh-line mr-1" />
                Recalculate
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Hero */}
      <DBEIHero report={report} />

      {/* Pillars */}
      <PillarBreakdown pillars={report.pillars} />

      {/* Channel Strength (CS sub-breakdown) */}
      {Object.keys(csChannels).length > 0 && <ChannelStrength channels={csChannels} />}

      {/* Narrative */}
      <NarrativeSection narrative={report.narrative} onRegenerate={regenerateNarrative} />

      {/* Metric Details */}
      {report.metrics && report.metrics.length > 0 && <MetricDetails metrics={report.metrics} />}
    </div>
  )
}
